<template>
  <div class="rsm-overlay">

    <!-- Intro screen: shown before chat starts -->
    <div v-if="phase === 'intro'" class="rsm-interruption">
      <div class="rsm-interruption__character"><img :src="capiImg" alt="Capi" class="rsm-capi-img rsm-capi-img--large" /></div>
      <div class="rsm-interruption__title">Capi</div>
      <p class="rsm-interruption__message">{{ introMessage }}</p>
      <button class="rsm-interruption__btn" @click="startChat">Clique pour continuer ➡️</button>
    </div>

    <!-- Chat UI -->
    <div v-else class="rsm-chat">

      <div class="rsm-chat__header">
        <img :src="capiImg" alt="Capi" class="rsm-capi-img rsm-capi-img--avatar" />
        <div>
          <div class="rsm-chat__name">Capi</div>
        </div>
      </div>

      <div class="rsm-chat__body" ref="chatBody">

        <!-- Archived exchanges -->
        <div v-for="(ex, i) in exchanges" :key="'ex' + i">
          <div v-if="ex.botMessage" class="rsm-message">
            <img :src="capiImg" alt="" class="rsm-capi-img rsm-capi-img--icon" />
            <div class="rsm-message__bubble rsm-message__bubble--pre">{{ ex.botMessage }}</div>
          </div>
          <div v-if="ex.userReply" class="rsm-user-reply">
            <div class="rsm-user-reply__bubble">{{ ex.userReply }}</div>
          </div>
        </div>

        <!-- Current step bot message -->
        <div v-if="currentStep && resolvedBotMessage" class="rsm-message">
          <img :src="capiImg" alt="" class="rsm-capi-img rsm-capi-img--icon" />
          <div class="rsm-message__bubble rsm-message__bubble--pre">{{ resolvedBotMessage }}</div>
        </div>

        <!-- 'continue' type: single centred button -->
        <div v-if="currentStep && currentStep.type === 'continue'" class="rsm-options rsm-options--continue">
          <button class="rsm-option-btn rsm-option-btn--continue" @click="selectOption(currentStep.options[0])">
            {{ currentStep.options[0].label }}
          </button>
        </div>

        <!-- 'question' type: pill options -->
        <div v-if="currentStep && currentStep.type === 'question'" class="rsm-options">
          <button
            v-for="(opt, i) in currentStep.options"
            :key="i"
            class="rsm-option-btn"
            @click="selectOption(opt)"
          >{{ opt.label }}</button>
        </div>

        <!-- 'multi-select' type: pick exactly N -->
        <div v-if="currentStep && currentStep.type === 'multi-select'" class="rsm-options rsm-options--multi">
          <div class="rsm-multi-count">{{ multiSelectChosen.length }}/{{ currentStep.count }} sélectionnées</div>
          <button
            v-for="(opt, i) in currentStep.options"
            :key="i"
            class="rsm-option-btn"
            :class="{ 'rsm-option-btn--selected': multiSelectChosen.includes(opt.label) }"
            @click="toggleMultiSelect(opt.label)"
          >{{ opt.label }}</button>
          <button
            class="rsm-option-btn rsm-option-btn--confirm"
            :disabled="multiSelectChosen.length !== currentStep.count"
            @click="confirmMultiSelect"
          >Continuer ➡️</button>
        </div>

        <!-- 'free-text' type: textarea + submit -->
        <div v-if="currentStep && currentStep.type === 'free-text'" class="rsm-free-text">
          <textarea
            v-model="freeTextValue"
            class="rsm-free-text__input"
            :placeholder="currentStep.placeholder || ''"
            rows="3"
          ></textarea>
          <button
            class="rsm-option-btn rsm-option-btn--confirm"
            :disabled="!freeTextValue.trim()"
            @click="submitFreeText"
          >J'ai répondu ➡️</button>
        </div>

      </div>

      <div class="rsm-chat__footer">
        <!-- footer intentionally empty: flow ends via [return to feed] option -->
      </div>

    </div><!-- end rsm-chat -->
  </div>
</template>

<script>
import { STEPS } from '../../data/chatbot-script.js';
import capiImg from '../../assets/capi.png';

export default {
  data() {
    return {
      capiImg,
      phase: 'intro',
      currentStepId: 'q1',
      exchanges: [],
      answers: [],
      lastEmotion: '',
      multiSelectChosen: [],
      freeTextValue: '',
      exerciseFunctions: [],
      stepTimings: {},
      _stepStart: null,
      freeTextWordCount: 0,
    };
  },

  computed: {
    introMessage() {
      const q0 = STEPS.find(s => s.id === 'q0');
      return q0 ? q0.botMessage : '';
    },
    currentStep() {
      return STEPS.find(s => s.id === this.currentStepId) || null;
    },
    resolvedBotMessage() {
      if (!this.currentStep) return '';
      return this.currentStep.botMessage.replace('{{emotion}}', this.lastEmotion);
    },
  },

  methods: {
    startChat() {
      this.phase = 'chat';
      this._stepStart = Date.now();
    },

    _recordTiming(stepId) {
      if (this._stepStart) {
        this.stepTimings[stepId] = Math.round((Date.now() - this._stepStart) / 1000);
        this._stepStart = Date.now();
      }
    },

    selectOption(opt) {
      const step = this.currentStep;

      this._recordTiming(step.id);

      if (opt.saveEmotion) {
        this.lastEmotion = opt.label;
      }

      this.exchanges.push({
        botMessage: this.resolvedBotMessage,
        userReply: opt.label,
      });
      this.answers.push({ stepId: step.id, answer: opt.label });

      if (opt.next === '[return to feed]') {
        this._finish();
        return;
      }

      this.currentStepId = opt.next;
      this._scrollToBottom();
    },

    toggleMultiSelect(label) {
      const idx = this.multiSelectChosen.indexOf(label);
      if (idx !== -1) {
        this.multiSelectChosen.splice(idx, 1);
      } else if (this.multiSelectChosen.length < this.currentStep.count) {
        this.multiSelectChosen.push(label);
      }
    },

    confirmMultiSelect() {
      const step = this.currentStep;
      const chosen = this.multiSelectChosen.slice();
      this.exerciseFunctions = chosen;

      this._recordTiming(step.id);

      this.exchanges.push({
        botMessage: this.resolvedBotMessage,
        userReply: chosen.join(', '),
      });
      this.answers.push({ stepId: step.id, answer: chosen });

      this.multiSelectChosen = [];
      this.currentStepId = step.next;
      this._scrollToBottom();
    },

    submitFreeText() {
      const step = this.currentStep;
      const text = this.freeTextValue.trim();

      this._recordTiming(step.id);
      this.freeTextWordCount = text.split(/\s+/).filter(Boolean).length;

      this.exchanges.push({
        botMessage: this.resolvedBotMessage,
        userReply: text,
      });
      this.answers.push({ stepId: step.id, answer: text });

      this.freeTextValue = '';
      this.currentStepId = step.next;
      this._scrollToBottom();
    },

    _finish() {
      this.$emit('chatbot-complete', {
        answers: this.answers,
        exerciseSelected: this.exerciseFunctions.length ? this.exerciseFunctions : null,
        exerciseCompleted: this.exerciseFunctions.length > 0,
        interactionAgent: {
          nbMots: this.freeTextWordCount,
          stepTimings: Object.assign({}, this.stepTimings),
        },
      });
    },

    _scrollToBottom() {
      this.$nextTick(() => {
        const body = this.$refs.chatBody;
        if (body) body.scrollTop = body.scrollHeight;
      });
    },
  },
};
</script>
